import { LotEventTypes, LotKinds, LotStatus } from '@parallel-fluidics/shared/constants';
import {
  allocateBomConsumption,
  formatLotCode,
  lotCounterFromFirestore,
  lotCounterToFirestore,
  lotDateKey,
  lotEventToFirestore,
  lotToFirestore,
} from '@parallel-fluidics/shared/models/lots';
import { pPartFromFirestore } from '@parallel-fluidics/shared/models/projects/parts';
import {
  collection,
  doc,
  getFirestore,
  runTransaction,
  serverTimestamp,
} from 'firebase/firestore';

import { BOM_NO_LONGER_COVERED, consumeLot, readLotsForConsumption } from './lotConsumption';

/**
 * Create an assembled lot from scanned source bags, in one transaction:
 * allocate the daily code, write the new lot (Awaiting inspection, with
 * sourceLotIds as the genealogy graph), and consume the sources.
 *
 * Consumption is allocated per BOM line greedily in scan order (two bags
 * of the same item split the charge), each source gets a `consumed`
 * event with its actual share, sealed bags auto-open, and a bag whose
 * remaining quantity hits zero is auto-marked Empty. The output part's
 * BOM is read fresh inside the transaction and is a hard rule: a part
 * without one can't be assembled (its bags would be consumed untracked
 * and stay Open forever — the 28-bag seal problem), and the assembly
 * fails if any line is no longer covered by the re-read sources (or a bag
 * was quarantined since scanning) — never silently under-consumes. Bags
 * matching no BOM line are consumed without a quantity (warning-only on
 * the page, which previews against the BOM it loaded).
 *
 * @returns {Promise<{ id: string, code: string }>} New lot doc ID and code.
 */
export default async function assembleLot({
  projectDocId,
  pPartDocId,
  material = null,
  initialQty,
  sourceLotIds,
  user,
}) {
  const db = getFirestore();
  const now = new Date();
  const counterRef = doc(db, 'lotCounters', lotDateKey(now));
  const lotRef = doc(collection(db, 'lots'));

  const code = await runTransaction(db, async (transaction) => {
    // All reads first: the counter, the output part (for its current BOM —
    // not the one the page loaded, which may have changed since), then
    // fresh copies of every source bag.
    const counterDoc = await transaction.get(counterRef);
    const count = (counterDoc.exists() ? lotCounterFromFirestore(counterDoc).count ?? 0 : 0) + 1;
    const lotCode = formatLotCode(now, count);

    const partSnap = await transaction.get(doc(db, 'projects', projectDocId, 'parts', pPartDocId));
    if (!partSnap.exists()) throw new Error('Output part not found');
    const components = pPartFromFirestore(partSnap).components || [];
    if (components.length === 0) {
      throw new Error('This part has no bill of materials — add one on the part page before assembling it');
    }

    const sources = await readLotsForConsumption(transaction, sourceLotIds);

    const { lines, deltaByLotId } = allocateBomConsumption(initialQty, components, sources);
    if (lines.some((line) => line.short > 0)) {
      throw new Error(BOM_NO_LONGER_COVERED);
    }

    transaction.set(counterRef, lotCounterToFirestore({ count }, serverTimestamp));
    transaction.set(lotRef, lotToFirestore({
      code: lotCode,
      kind: LotKinds.ASSEMBLED,
      projectDocId,
      pPartDocId,
      material,
      initialQty,
      remainingQty: initialQty,
      status: LotStatus.AWAITING_INSPECTION,
      sourceLotIds,
      _createdBy: user,
    }, serverTimestamp));
    transaction.set(doc(collection(lotRef, 'events')), lotEventToFirestore({
      type: LotEventTypes.CREATED,
      deltaQty: initialQty,
      _createdBy: user,
    }, serverTimestamp));

    sources.forEach((source) => {
      consumeLot(transaction, {
        lot: source,
        deltaQty: deltaByLotId[source.id] ?? null,
        reason: `Assembled into ${lotCode}`,
        via: 'assembly',
        relatedIds: { lotId: lotRef.id },
        user,
      });
    });

    return lotCode;
  });

  return { id: lotRef.id, code };
}
